import { LanguagePicker } from './LanguagePicker';
import { RichText } from '../i18n/RichText';
import { WIZARD_LANGUAGES_ENABLED, currentUiLang, withUiLang } from '../i18n/feature';
import { t } from '../services/language-manager';
import logo from '../assets/mediabriz-logo-svg-400px.svg';

interface LandingPageProps {
  insightsUrl: string;
  /** CloudBriz Admin (whatever the org flags). */
  showSettings?: boolean;
  /** CloudBriz Admin, and Conversation Actions is on (`aiActionsEnabled`; missing = enabled). */
  showActions?: boolean;
  /** No CloudBriz Admin: Settings needs it in every org. */
  showAdminNote?: boolean;
  /** Operator flag; false for a Login Limit only org (Insights shows Login Limit only). */
  conversationsEnabled?: boolean;
  onOpenSettings?: () => void;
  onOpenActions?: () => void;
}

function MenuChevron() {
  return (
    <svg className="landing-menu__chevron" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M6.5 4.5 11 9l-4.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Post-install re-entry hub inside the Genesys Apps iframe (already-registered orgs).
// Insights is always available (first). The dashboard loads for CloudBriz View
// or CloudBriz Admin. Conversation Actions then Settings appear when the user
// has CloudBriz Admin; Actions also needs its org flag. Without Admin, a note
// says Admin is required to change them.
// Settings is shown to every CloudBriz Admin, whatever the org flags.
// The Guard block inside Settings still renders only when scanEnabled is true.
// The Insights CTA uses a named window.open target so a second click re-focuses the
// existing tab instead of opening a duplicate. A real button click is a user gesture,
// so the new tab is not treated as a blocked popup.
export function LandingPage({
  insightsUrl,
  showSettings = false,
  showActions = false,
  showAdminNote = false,
  conversationsEnabled = true,
  onOpenSettings,
  onOpenActions,
}: LandingPageProps) {
  const hasMenu = Boolean(
    (showSettings && onOpenSettings) || (showActions && onOpenActions),
  );

  const openInsights = () => {
    window.open(withUiLang(insightsUrl, currentUiLang()), 'mediabriz-insights', 'noopener,noreferrer');
  };

  return (
    <div className={`card landing-page${hasMenu ? ' landing-page--hub' : ''}`}>
      {WIZARD_LANGUAGES_ENABLED && (
        <div className="landing-page__toolbar">
          <LanguagePicker />
        </div>
      )}
      <img src={logo} alt="Mediabriz" className="landing-page__logo" />
      <p className="landing-page__headline">
        {t(hasMenu ? 'landing-hub-headline' : conversationsEnabled ? 'landing-headline' : 'landing-headline-login-limit')}
      </p>

      {hasMenu ? (
        <nav className="landing-menu" aria-label={t('landing-menu-label')}>
          <button type="button" className="landing-menu__item landing-menu__item--primary" onClick={openInsights}>
            <span className="landing-menu__body">
              <span className="landing-menu__title">{t('landing-menu-insights-title')}</span>
              <span className="landing-menu__desc">
                {t(conversationsEnabled ? 'landing-menu-insights-desc' : 'landing-menu-insights-desc-login-limit')}
              </span>
            </span>
            <MenuChevron />
          </button>

          {showActions && onOpenActions && (
            <button type="button" className="landing-menu__item" onClick={onOpenActions}>
              <span className="landing-menu__body">
                <span className="landing-menu__title">{t('landing-menu-actions-title')}</span>
                <span className="landing-menu__desc">{t('landing-menu-actions-desc')}</span>
              </span>
              <MenuChevron />
            </button>
          )}

          {showSettings && onOpenSettings && (
            <button type="button" className="landing-menu__item" onClick={onOpenSettings}>
              <span className="landing-menu__body">
                <span className="landing-menu__title">{t('landing-menu-settings-title')}</span>
                <span className="landing-menu__desc">{t('landing-menu-settings-desc')}</span>
              </span>
              <MenuChevron />
            </button>
          )}
        </nav>
      ) : (
        <div className="btn-group landing-page__cta">
          <button className="btn btn--primary" onClick={openInsights}>
            {t('landing-open-cta')}
          </button>
        </div>
      )}

      {showAdminNote && (
        <p className="landing-page__admin-note">
          <RichText
            k={conversationsEnabled ? 'complete-step-3-admin' : 'complete-step-3-admin-login-limit'}
            values={{
              admin: <code className="permission-code" dir="ltr">integration:cloudBriz:admin</code>,
            }}
          />
        </p>
      )}

      <p className="landing-page__support">
        <RichText
          k="landing-support"
          values={{
            support: (
              <a href="https://support.mediabriz.com" target="_blank" rel="noopener noreferrer">
                {t('landing-support-general-link')}
              </a>
            ),
          }}
        />
      </p>
    </div>
  );
}
